import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatRM } from "@/lib/api";
import { ChevronDown, ChevronRight, Users } from "lucide-react";

export default function AdminOrgChart() {
  const { t } = useTranslation();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/org-chart")
      .then((r) => {
        setTree(r.data);
        // Expand nodes that have downlines by default
        const init = {};
        r.data.forEach((n) => { if (n.downline_count > 0) init[n.agent.id] = true; });
        setExpanded(init);
      })
      .catch(() => setTree([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const filtered = tree.filter((n) => {
    if (!q) return true;
    const term = q.toLowerCase();
    return (
      (n.agent.name || "").toLowerCase().includes(term) ||
      (n.agent.email || "").toLowerCase().includes(term) ||
      (n.agent.referral_code || "").toLowerCase().includes(term) ||
      n.downlines.some((d) => (d.name || "").toLowerCase().includes(term) || (d.email || "").toLowerCase().includes(term))
    );
  });

  return (
    <div className="p-6 md:p-10" data-testid="admin-orgchart-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP2.orgChart")}</h1>

      <input
        placeholder={t("common.search")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="mt-6 w-full sm:max-w-sm px-4 py-2 rounded-full border border-[#e5ebeb] focus:border-[#00868e] outline-none text-sm"
        data-testid="orgchart-search"
      />

      <div className="mt-6 space-y-3" data-testid="orgchart-tree">
        {loading ? (
          <div className="text-[#4a6e77]">{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-[#e5ebeb] bg-white p-10 text-center text-[#4a6e77]">
            {t("common.empty")}
          </div>
        ) : (
          filtered.map((node) => {
            const open = expanded[node.agent.id];
            return (
              <div key={node.agent.id} className="rounded-lg border border-[#e5ebeb] bg-white" data-testid={`org-agent-${node.agent.id}`}>
                <button
                  onClick={() => toggle(node.agent.id)}
                  className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#fdfbf7] transition-colors duration-150"
                >
                  {open ? <ChevronDown size={16} className="text-[#4a6e77]" /> : <ChevronRight size={16} className="text-[#4a6e77]" />}
                  <div className="h-10 w-10 rounded-full bg-[#005870]/10 text-[#005870] flex items-center justify-center font-serif-cn font-semibold">
                    {(node.agent.name || "?").charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#003645] truncate">{node.agent.name}</div>
                    <div className="text-xs text-[#4a6e77]">
                      {node.agent.email} · <span className="font-mono tracking-wider">{node.agent.referral_code || "-"}</span>
                    </div>
                  </div>
                  <div className="hidden sm:block text-right shrink-0">
                    <div className="text-xs text-[#4a6e77]">{t("adminP2.downlines")}</div>
                    <div className="text-lg font-semibold text-[#005870]">{node.downline_count}</div>
                  </div>
                  <div className="hidden md:block text-right shrink-0">
                    <div className="text-xs text-[#4a6e77]">{t("agentHub.withdrawable")}</div>
                    <div className="text-sm font-medium text-[#003645]">{formatRM(node.agent.agent_balance || 0)}</div>
                  </div>
                </button>
                {open && (
                  <div className="border-t border-[#e5ebeb] px-4 py-3">
                    {node.downlines.length === 0 ? (
                      <div className="text-xs text-[#4a6e77] py-2">{t("adminP2.noDownlines")}</div>
                    ) : (
                      <ul className="divide-y divide-[#e5ebeb]">
                        {node.downlines.map((d) => (
                          <li key={d.id} className="py-2 flex items-center gap-3">
                            <Users size={14} className="text-[#00868e]" />
                            <span className="text-[#003645] flex-1 truncate">{d.name}</span>
                            <span className="text-xs text-[#4a6e77]">{d.email}</span>
                            <span className="text-xs text-[#4a6e77] px-2 py-0.5 rounded-full bg-[#e5ebeb]/50">{d.role}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
